import { useApi } from '@repo-level/core-front/hooks';
import { getPlayerAccounts } from '@repo-level/core-profile/api/profile';
import type { FC } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { useNavigation } from 'react-navi';
import type { CompPoints, User, Wallet } from '../../api/auth';
import { CommonContext } from '../common';
import { PrimarySettingsContext } from '../primarySettings';
import {
  changeUserWallet as actionChangeUserWallet,
  deleteUserWallet as actionDeleteUserWallet,
  fetchPlayerSettings as actionFetchPlayerSettings,
  fetchUser as actionFetchUser,
  insertUser as actionInsertUser,
  refreshUser as actionRefreshUser,
  resetAfterLoginRedirect as actionResetAfterLoginRedirect,
  resetUser as actionResetUser,
  setAfterLoginRedirect as actionSetAfterLoginRedirect,
  setCompPoints as actionSetCompPoints,
  setCurrentAmount as actionSetCurrentAmount,
  setCurrentPaymentMethodId as actionSetCurrentPaymentMethodId,
  updateUser as actionUpdateUser,
  updateUserReason as actionUpdateUserReason,
  updateUserWallet as actionUpdateUserWallet,
  setAccount,
} from './actions';
import type { UserState } from './reducer';
import {
  REASON_FOR_UPDATING_USER,
  USER_ACTION_TYPE,
  userReducer,
} from './reducer';

type AuthContextData = UserState;
interface AuthContextActions {
  fetchUser: (
    reasonForUpdatingUser: REASON_FOR_UPDATING_USER
  ) => Promise<User | void>;
  insertUser: (
    user: User,
    reasonForUpdatingUser: REASON_FOR_UPDATING_USER
  ) => Promise<User | void>;
  updateUserReason: (reason: REASON_FOR_UPDATING_USER) => Promise<User | void>;
  resetUser: () => Promise<User | void>;
  refreshUser: () => Promise<User | void>;

  changeUserWallet: (currency: Wallet['currency']) => Promise<User | void>;
  deleteUserWallet: (currency: Wallet['currency']) => Promise<User | void>;
  updateUserWallet: () => Promise<User | void>;

  setCurrentAmount: (amount: number | string) => void;
  updateUser: (user: Partial<User>, reason?: REASON_FOR_UPDATING_USER) => void;
  updateCompPoints: (compPoints: CompPoints | undefined) => void;

  setAfterLoginRedirect: (url: string) => void;

  setSubmitPaymentPending: (pending: boolean) => void;

  setCurrentPaymentMethodId: (
    currentPaymentMethodId: string | undefined
  ) => void;
}

export const AuthContext = createContext<AuthContextData & AuthContextActions>(
  null as any
);

const initialState: UserState = {
  user: undefined,
  account: null,
  compPoints: undefined,
  reasonForUpdatingUser: undefined,
  isAuthenticated: false,
  pendingUser: true,
  wallets: [],
  extendedWallets: [],
  pendingWallets: false,
  currentAmount: null,
  afterLoginRedirectUrl: undefined,
  pendingPlayerSettings: true,
  playerSettings: undefined,
  pendingSubmitPayment: false,
  currentPaymentMethodId: undefined,
};

const USER_REFRESH_INTERVAL = 60e3;

export const AuthContextProvider: FC = ({ children }) => {
  const [state, dispatch] = useReducer(userReducer, initialState);

  const { updateDefaultUserStatuses } = useContext(PrimarySettingsContext);
  const { currentIp } = useContext(CommonContext);

  const navigation = useNavigation();

  const [requestGetPlayerAccounts, { data: accounts }] =
    useApi(getPlayerAccounts);

  const updateDefaultUserStatusesWithIp = useMemo(
    () => updateDefaultUserStatuses(currentIp?.countryCode),
    [updateDefaultUserStatuses, currentIp?.countryCode]
  );

  const fetchUser = useCallback(
    async (reasonForUpdatingUser: REASON_FOR_UPDATING_USER) =>
      actionFetchUser(dispatch, {
        reasonForUpdatingUser,
        defaultState: initialState,
        updateDefaultUserStatuses: updateDefaultUserStatusesWithIp,
      }),
    [dispatch, updateDefaultUserStatusesWithIp]
  );

  useEffect(() => {
    if (!state.isAuthenticated) return;

    const interval = setInterval(
      () =>
        actionRefreshUser(dispatch, {
          prevUser: state.user,
          updateDefaultUserStatuses: updateDefaultUserStatusesWithIp,
        }),
      USER_REFRESH_INTERVAL
    );
    return () => {
      clearInterval(interval);
    };
  }, [
    dispatch,
    state.isAuthenticated,
    state.user,
    updateDefaultUserStatusesWithIp,
  ]);

  const refreshUser = useCallback(
    () =>
      actionRefreshUser(dispatch, {
        prevUser: state.user,
        updateDefaultUserStatuses: updateDefaultUserStatusesWithIp,
      }),
    [updateDefaultUserStatusesWithIp, state.user]
  );

  const insertUser = useCallback(
    (user: User, reasonForUpdatingUser: REASON_FOR_UPDATING_USER) =>
      actionInsertUser(dispatch, { user, reasonForUpdatingUser }),
    [dispatch]
  );

  const resetUser = useCallback(
    () => actionResetUser(dispatch, { navigation, defaultState: initialState }),
    [dispatch, navigation]
  );

  const updateUser = useCallback(
    (user: Partial<User>) => {
      actionUpdateUser(dispatch, { user });
    },
    [dispatch]
  );

  const updateUserReason = useCallback(
    (reason: REASON_FOR_UPDATING_USER) =>
      actionUpdateUserReason(dispatch, {
        reasonForUpdatingUser: reason,
      }),
    [dispatch]
  );

  const changeUserWallet = useCallback(
    async (currency: Wallet['currency']) =>
      actionChangeUserWallet(dispatch, {
        currency,
        defaultState: initialState,
        updateDefaultUserStatuses: updateDefaultUserStatusesWithIp,
      }),
    [dispatch, updateDefaultUserStatusesWithIp]
  );

  const updateUserWallet = useCallback(
    () =>
      actionUpdateUserWallet(dispatch, {
        defaultState: initialState,
      }),
    [dispatch]
  );

  const deleteUserWallet = useCallback(
    async (currency: Wallet['currency']) => {
      await actionDeleteUserWallet(dispatch, {
        currency,
        defaultState: initialState,
        updateDefaultUserStatuses: updateDefaultUserStatusesWithIp,
      });

      if (currency === state.user?.currency) {
        changeUserWallet(
          state.wallets[0].currency === currency
            ? state.wallets[1].currency
            : state.wallets[0].currency
        );
      }
    },
    [
      dispatch,
      updateDefaultUserStatusesWithIp,
      changeUserWallet,
      state.user,
      state.wallets,
    ]
  );

  const setCurrentAmount = useCallback(
    (currentAmount: number | string) => {
      actionSetCurrentAmount(dispatch, { currentAmount });
    },
    [dispatch]
  );

  const updateCompPoints = useCallback(
    (compPoints: CompPoints | undefined) => {
      actionSetCompPoints(dispatch, { compPoints });
    },
    [dispatch]
  );

  const setAfterLoginRedirect = useCallback(
    (url: string) => {
      actionSetAfterLoginRedirect(dispatch, { url });
    },
    [dispatch]
  );

  const setCurrentPaymentMethodId = useCallback(
    (currentPaymentMethodId: string | undefined) => {
      actionSetCurrentPaymentMethodId(dispatch, { currentPaymentMethodId });
    },
    [dispatch]
  );

  const setSubmitPaymentPending = useCallback(
    (pending: boolean) => {
      dispatch({
        type: pending
          ? USER_ACTION_TYPE.PLAYER_SUBMIT_PAYMENT_START_PENDING
          : USER_ACTION_TYPE.PLAYER_SUBMIT_PAYMENT_STOP_PENDING,
      });
    },
    [dispatch]
  );

  useEffect(() => {
    requestGetPlayerAccounts();
  }, [requestGetPlayerAccounts, state.wallets]);

  useEffect(() => {
    if (!(accounts && state.user)) return;

    const wallet =
      state.wallets.find(({ currency }) => currency === state.user?.currency) ||
      state.wallets[0];

    const account =
      accounts.find(({ currency }) => currency === wallet.currency) ||
      accounts[0];

    setAccount(dispatch, { account });
  }, [accounts, state.user, state.wallets]);

  useEffect(() => {
    if (state.isAuthenticated === false || state.afterLoginRedirectUrl == null)
      return;
    window.location.href = state.afterLoginRedirectUrl;
    actionResetAfterLoginRedirect(dispatch, {});
  }, [dispatch, state.isAuthenticated, state.afterLoginRedirectUrl]);

  useEffect(() => {
    fetchUser(REASON_FOR_UPDATING_USER.VISIT);
  }, [fetchUser]);

  useEffect(() => {
    if (state.user == null || state.user.id == null) return;
  }, [state.user]);

  useEffect(() => {
    if (state.pendingUser) return;

    actionFetchPlayerSettings(dispatch, {});
  }, [state.pendingUser, state.isAuthenticated]);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        refreshUser,
        fetchUser,
        insertUser,
        resetUser,
        updateUserReason,
        changeUserWallet,
        deleteUserWallet,
        setCurrentAmount,
        updateUser,
        updateCompPoints,
        setAfterLoginRedirect,
        updateUserWallet,
        setSubmitPaymentPending,
        setCurrentPaymentMethodId,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
